import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Col, DatePicker, Form, Input, Row, Card, message, Spin } from "antd"
import { useWatch } from "antd/es/form/Form"
import { useDispatch, useSelector } from "react-redux"
import { debounce } from "lodash"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { getExistingClientDetail } from "store/bookings"

import { CustomSelect, PhoneInput } from "ui/Input"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import { LocationInfo } from "pagesV2/pro/Experiences/ListingLocation/LocationForm"
import useGeocoder from "modules/google/hooks/useGeocoder"
import usePredictions from "modules/google/hooks/usePredictions"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { cleanPastBooking } from "store/bookings"
import GroupManager from "pagesV2/pro/clients/ClientWindow/GroupManager"
import PropTypes from "prop-types"
import { getGroups } from "store/groups"
import { handleLastNameBlur, handleLastNameInput, handleLastNameKeyPress } from "pagesV2/pro/utils/ProUtils"
import { handleNameKeyDown, isSpaceKey, sanitizeSingleWord } from "utils/InputSanitizers"

const ClientForm = ({ form }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const geocoder = useGeocoder(null)
  const [selectPlace] = usePredictions()
  const [meetingLatLng, setMeetingLatLng] = useState(null)
  const [manualAddress, setManualAddress] = useState(false)
  const [avatar, setAvatar] = useState(() => form.getFieldValue("avatar") || null)
  const watchedGroups = Form.useWatch("client_groups", form)
  const selectedGroups = watchedGroups || []
  const clients = useSelector((state) => state.clients.client)
  const isSearching = useSelector((state) => state.bookings.existing_clients_list.loading)
  const [centerLatLng, setCenterLatLng] = useState(null)
  const [phoneError, setPhoneError] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const phone = useWatch("phone_number", form)
  const email = useWatch("email", form)
  console.log(centerLatLng)
  useEffect(() => {
    return () => dispatch(cleanPastBooking())
  }, [dispatch])

  const genderOptions = [
    { label: t("settings.gender.male"), value: "male" },
    { label: t("settings.gender.female"), value: "female" },
    { label: t("settings.gender.other"), value: "other" }
  ]

  const changeHandlers = ({ target: { name, value } }) => {
    form.setFieldsValue({ [name]: value })
  }

  useEffect(() => {
    if (clients?.user?.avatar) {
      setAvatar(clients?.user?.avatar)
    }
  }, [clients?.user?.avatar])

  const debounceCoordinatesToAddress = useCallback(
    debounce(
      (location) =>
        geocoder({ location }, (place) => {
          changeHandlers({ target: { name: "address", value: place.formatted_address } })
        }),
      200
    ),
    [geocoder]
  )

  const placeChangedHandler = useCallback(
    ({ target: { coordinates, value } }) => {
      if (coordinates) debounceCoordinatesToAddress(coordinates)
      if (value) {
        setMeetingLatLng(value)
      }
    },
    [debounceCoordinatesToAddress]
  )

  useEffect(() => {
    const address = clients?.user
    if (address?.address_line_1) {
      setMeetingLatLng(address.address_line_1)
    }
    if (address?.city || address?.state || address?.country || address?.zip_code) {
      setManualAddress(true)
      setMeetingLatLng(`${address.city}, ${address.state}, ${address.country}, ${address.zip_code}`)
    }
  }, [clients])

  useEffect(() => {
    form.setFieldsValue({
      manualAddress: manualAddress
    })
  }, [manualAddress])

  useEffect(() => {
    if (!meetingLatLng) {
      form.setFieldsValue({ address: "" })
    }
  }, [meetingLatLng, form])

  const onUpload = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target.result
      form.setFieldsValue({ avatar: file })
      setAvatar(base64)
    }
    reader.readAsDataURL(file)
  }

  const handleImageChange = async (e) => {
    form.setFields([{ name: "avatar", errors: [] }])
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        message.error("File size must be less than 5 MB")
        return
      }
      onUpload(file)
    }
  }

  // Fetch groups on component mount
  useEffect(() => {
    dispatch(getGroups())
  }, [dispatch])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidateEmail = useCallback(
    debounce(async (emailVal) => {
      const res = await dispatch(getExistingClientDetail({ q: emailVal }))
      const matched = Array.isArray(res) ? res[0] : res
      if (matched?.id) {
        setEmailError(t("client.error_messages.email_already_exists"))
        form.setFields([{ name: "email", errors: [t("client.error_messages.email_already_exists")] }])
      } else {
        setEmailError(null)
        form.setFields([{ name: "email", errors: [] }])
      }
    }, 800),
    [dispatch, t, form]
  )

  useEffect(() => {
    return () => {
      debouncedValidateEmail.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const currentEmail = clients?.user?.email
    if (email && email !== currentEmail) {
      debouncedValidateEmail(email)
    } else {
      debouncedValidateEmail.cancel()
      setEmailError(null)
      form.setFields([{ name: "email", errors: [] }])
    }
  }, [email, clients?.user?.email, debouncedValidateEmail, form])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidatePhone = useCallback(
    debounce(async (phoneVal) => {
      try {
        await validatePhoneNumber(phoneVal)
      } catch {
        return
      }
      const res = await dispatch(getExistingClientDetail({ q: phoneVal }))
      const matched = Array.isArray(res) ? res[0] : res
      if (matched?.id) {
        setPhoneError(t("client.error_messages.phone_already_exists"))
        form.setFields([{ name: "phone_number", errors: [t("client.error_messages.phone_already_exists")] }])
      } else {
        setPhoneError(null)
        form.setFields([{ name: "phone_number", errors: [] }])
      }
    }, 800),
    [dispatch, t, form]
  )

  useEffect(() => {
    return () => {
      debouncedValidatePhone.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const phoneVal = phone?.fullNumber
    const currentPhone = clients?.user?.phone_number
    if (phoneVal && phoneVal !== currentPhone) {
      debouncedValidatePhone(phoneVal)
    } else {
      debouncedValidatePhone.cancel()
      setPhoneError(null)
      form.setFields([{ name: "phone_number", errors: [] }])
    }
  }, [phone, clients?.user?.phone_number, debouncedValidatePhone, form])

  const onInputChange = (value) => {
    form.setFieldsValue({ phone_number: value })
  }

  const handleSelectedGroupsChange = (updatedGroups) => {
    form.setFieldsValue({ client_groups: updatedGroups })
  }

  const handleDateKeyDown = (e) => {
    if (e.key.length === 1 && !/\d/.test(e.key) && e.key !== "/" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
    }
  }

  return (
    <Spin spinning={isSearching}>
    <Card className="mb-3xl" layout="vertical">
      <h4 className="mb-s"> {t("client.personal_info")}</h4>
      <Form.Item name="avatar" className="mb-l">
        <AddAvatar avatar={avatar} handleImageChange={handleImageChange} />
      </Form.Item>
      <Row gutter={[20, 0]}>
        <Col span={24}>
          <Form.Item label={t("client.filters.groups")} name="client_groups">
            <GroupManager selectedGroups={selectedGroups} moduleName="client" onSelectedGroupsChange={handleSelectedGroupsChange} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="first_name"
            label={t("client.first_name")}
            rules={[{ required: true, message: t("client.error_messages.first_name_required") }]}
          >
            <Input onKeyDown={handleNameKeyDown} placeholder={t("client.first_name")} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="last_name"
            label={t("client.last_name")}
            rules={[{ required: true, message: t("client.error_messages.last_name_required") }]}
          >
            <Input
              onKeyDown={handleLastNameKeyPress}
              onInput={handleLastNameInput}
              placeholder={t("client.last_name")}
              onBlur={(e) => handleLastNameBlur(e, form)}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            name="email"
            label={t("client.email")}
            dependencies={["phone_number"]}
            getValueFromEvent={(e) => sanitizeSingleWord(e.target.value)}
            rules={[
              { type: "email", message: t("client.error_messages.email_invalid") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const phone = getFieldValue("phone_number")
                  if (!value && !phone?.fullNumber) {
                    return Promise.reject(new Error(t("client.error_messages.phone_or_email_required")))
                  }
                  if (emailError) {
                    return Promise.reject(new Error(emailError))
                  }
                  return Promise.resolve()
                }
              })
            ]}
          >
            <Input
              placeholder={t("client.enter_email")}
              onKeyDown={(e) => isSpaceKey(e) && e.preventDefault()}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item
            label={t("sign_up.phone_number")}
            name="phone_number"
            dependencies={["email"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const email = getFieldValue("email")
                  if (!value?.fullNumber && !email) {
                    return Promise.reject(new Error(t("client.error_messages.phone_or_email_required")))
                  }
                  if (phoneError) {
                    return Promise.reject(new Error(phoneError))
                  }
                  if (value?.fullNumber) {
                    return validatePhoneNumber(value.fullNumber)
                  }
                  return Promise.resolve()
                }
              })
            ]}
          >
            <PhoneInput
              key={clients.user.phone_number}
              initialValue={clients?.user?.phone_number}
              initialCountryCode={clients?.user?.country_code}
              onChange={onInputChange}
            />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item name="gender" label={t("client.select_gender")}>
            <CustomSelect options={genderOptions} placeholder={t("client.select_gender")} />
          </Form.Item>
        </Col>
        <Col span={24} md={12}>
          <Form.Item name="birthday" label={t("client.birthday")}>
            <DatePicker
              style={{ width: "100%", height: "44px" }}
              placeholder="mm/dd/yyyy"
              format="MM/DD/YYYY"
              disabledDate={(current) => current && current > Date.now()}
              onKeyDown={handleDateKeyDown}
            />
          </Form.Item>
        </Col>
      </Row>
      {manualAddress ? (
        <Form.Item name="manualAddress">
          <LocationInfo
            backBtnManualText={t("client.back_to_manual_address")}
            isRequired={false}
            screenName="Client"
            form={form}
            t={t}
            setCenterLatLng={setCenterLatLng}
            setManualAddress={setManualAddress}
            setMeetingLatLng={setMeetingLatLng}
            selectPlace={selectPlace}
          />
        </Form.Item>
      ) : (
        <Form.Item label={t("client.address")} name="address_line_1">
          <GooglePlacesNewInput
            placeholder="Enter your address"
            bsSize="sm"
            onChange={placeChangedHandler}
            dropDownMenuClassName="mt-2 shadow-sm"
            resetIfChanged
            meetingLatLng={meetingLatLng}
            manualAddress={manualAddress}
            setManualAddress={setManualAddress}
            setMeetingLatLng={setMeetingLatLng}
          />
        </Form.Item>
      )}
    </Card>
    </Spin>
  )
}

ClientForm.propTypes = {
  form: PropTypes.object.isRequired
}

export default ClientForm
